// frontend/src/pages/Settings.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  LocalShipping as DeliveryIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [codeTypes, setCodeTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    field_label: 'Value',
    is_active: true,
  });

  // Heavy goods label settings (General Settings tab)
  const [heavyGoods, setHeavyGoods] = useState({ weight_threshold: null, label_filename: '', has_label: false });
  const [heavyGoodsThreshold, setHeavyGoodsThreshold] = useState('');
  const [heavyGoodsSaving, setHeavyGoodsSaving] = useState(false);

  usePageTitle('Settings');


  useEffect(() => {
    loadCodeTypes();
    loadHeavyGoods();
  }, []);

  const loadHeavyGoods = async () => {
    try {
      const response = await manufacturingAPI.heavyGoods.getSettings();
      setHeavyGoods(response.data);
      setHeavyGoodsThreshold(response.data.weight_threshold ?? '');
    } catch (error) {
      console.error('Error loading heavy goods settings:', error);
    }
  };

  const handleSaveHeavyGoodsThreshold = async () => {
    const raw = String(heavyGoodsThreshold).trim();
    if (raw !== '' && (isNaN(parseFloat(raw)) || parseFloat(raw) < 0)) {
      toast.error('Weight threshold must be a positive number');
      return;
    }
    setHeavyGoodsSaving(true);
    try {
      const formData = new FormData();
      formData.append('weight_threshold', raw);
      const response = await manufacturingAPI.heavyGoods.updateSettings(formData);
      setHeavyGoods(response.data);
      toast.success('Heavy goods weight threshold saved');
    } catch (error) {
      console.error('Error saving heavy goods threshold:', error);
      toast.error(error.response?.data?.error || 'Failed to save threshold');
    } finally {
      setHeavyGoodsSaving(false);
    }
  };

  const handleHeavyGoodsUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setHeavyGoodsSaving(true);
    try {
      const formData = new FormData();
      formData.append('label_pdf', file);
      const response = await manufacturingAPI.heavyGoods.updateSettings(formData);
      setHeavyGoods(response.data);
      toast.success('Heavy goods label uploaded');
    } catch (error) {
      console.error('Error uploading heavy goods label:', error);
      toast.error(error.response?.data?.error || 'Failed to upload label');
    } finally {
      setHeavyGoodsSaving(false);
    }
  };

  const handlePreviewHeavyGoodsLabel = async () => {
    try {
      const response = await manufacturingAPI.heavyGoods.getLabel();
      const byteChars = atob(response.data.label);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        bytes[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (error) {
      console.error('Error previewing heavy goods label:', error);
      toast.error('Failed to load label PDF');
    }
  };

  const handleDeleteHeavyGoodsLabel = async () => {
    if (!window.confirm('Remove the heavy goods label PDF?')) return;
    try {
      const response = await manufacturingAPI.heavyGoods.deleteLabel();
      setHeavyGoods(response.data);
      toast.success('Heavy goods label removed');
    } catch (error) {
      console.error('Error deleting heavy goods label:', error);
      toast.error('Failed to remove label');
    }
  };

    const loadCodeTypes = async () => {
    setLoading(true);
    try {
        const response = await manufacturingAPI.orderCodeTypes.list();
        // Handle paginated response
        setCodeTypes(response.data.results || response.data || []);
    } catch (error) {
        console.error('Error loading code types:', error);
        toast.error('Failed to load order code types');
        setCodeTypes([]); // Set empty array on error to prevent filter errors
    } finally {
        setLoading(false);
    }
    };

  const handleOpenDialog = (codeType = null) => {
    if (codeType) {
      setEditingCode(codeType);
      setFormData({
        code: codeType.code,
        name: codeType.name,
        description: codeType.description || '',
        field_label: codeType.field_label || 'Value',
        is_active: codeType.is_active,
      });
    } else {
      setEditingCode(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        field_label: 'Value',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCode(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      field_label: 'Value',
      is_active: true,
    });
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Code and Name are required');
      return;
    }

    try {
      if (editingCode) {
        await manufacturingAPI.orderCodeTypes.update(editingCode.id, formData);
        toast.success('Code type updated successfully');
      } else {
        await manufacturingAPI.orderCodeTypes.create(formData);
        toast.success('Code type created successfully');
      }
      handleCloseDialog();
      loadCodeTypes();
    } catch (error) {
      console.error('Error saving code type:', error);
      toast.error(error.response?.data?.error || 'Failed to save code type');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this code type?')) {
      return;
    }

    try {
      await manufacturingAPI.orderCodeTypes.delete(id);
      toast.success('Code type deleted successfully');
      loadCodeTypes();
    } catch (error) {
      console.error('Error deleting code type:', error);
      toast.error('Failed to delete code type');
    }
  };

  const handleToggleActive = async (codeType) => {
    try {
      await manufacturingAPI.orderCodeTypes.update(codeType.id, {
        ...codeType,
        is_active: !codeType.is_active,
      });
      toast.success(
        `Code type ${!codeType.is_active ? 'activated' : 'deactivated'}`
      );
      loadCodeTypes();
    } catch (error) {
      console.error('Error toggling code type:', error);
      toast.error('Failed to update code type');
    }
  };

  const getCodeIcon = (code) => {
    switch (code.toUpperCase()) {
      case 'BOOKIN':
        return <PhoneIcon />;
      case 'DELIVERY':
      case 'DELIVERY-NOTE':
        return <DeliveryIcon />;
      default:
        return <CodeIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              System Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure system-wide settings and parameters
            </Typography>
          </Box>
        </Box>

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Order Code Types" />
          <Tab label="General Settings" />
          <Tab label="Notifications" />
        </Tabs>

        {/* Order Code Types Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>What are Order Code Types?</strong>
              </Typography>
              <Typography variant="body2">
                Order Code Types define special codes that appear in Sage exports (like BOOKIN for contact numbers).
                When these codes are found during import, they're treated as metadata rather than products.
              </Typography>
            </Alert>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Code Types
                    </Typography>
                    <Typography variant="h4">
                      {codeTypes.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Active
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {codeTypes.filter(c => c.is_active).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Inactive
                    </Typography>
                    <Typography variant="h4" color="text.secondary">
                      {codeTypes.filter(c => !c.is_active).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add New Code Type
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Field Label</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codeTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No code types defined yet. Add one to get started!
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenDialog()}
                          sx={{ mt: 2 }}
                        >
                          Add First Code Type
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  codeTypes.map((codeType) => (
                    <TableRow key={codeType.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getCodeIcon(codeType.code)}
                          <Chip
                            label={codeType.code}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {codeType.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={codeType.field_label} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {codeType.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={codeType.is_active}
                          onChange={() => handleToggleActive(codeType)}
                          color="success"
                        />
                        <Typography variant="caption" display="block">
                          {codeType.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(codeType)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(codeType.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* General Settings Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PdfIcon color="primary" />
                <Typography variant="h6">Heavy Goods Label</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload the label PDF to print for heavy items, and set the per-unit weight
                at which the print button appears on DX Shipping.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  label="Weight threshold (kg)"
                  type="number"
                  size="small"
                  value={heavyGoodsThreshold}
                  onChange={(e) => setHeavyGoodsThreshold(e.target.value)}
                  helperText="Items weighing this much or more get the heavy goods label. Leave blank to disable."
                  inputProps={{ min: 0, step: 'any' }}
                  sx={{ width: 280 }}
                />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveHeavyGoodsThreshold}
                  disabled={heavyGoodsSaving}
                >
                  Save Threshold
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={heavyGoodsSaving}
                >
                  {heavyGoods.has_label ? 'Replace Label PDF' : 'Upload Label PDF'}
                  <input type="file" accept="application/pdf,.pdf" hidden onChange={handleHeavyGoodsUpload} />
                </Button>
                {heavyGoods.has_label ? (
                  <>
                    <Chip
                      icon={<PdfIcon />}
                      label={heavyGoods.label_filename || 'heavy-goods-label.pdf'}
                      color="success"
                      variant="outlined"
                    />
                    <IconButton size="small" color="primary" onClick={handlePreviewHeavyGoodsLabel} title="Preview PDF">
                      <PreviewIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleDeleteHeavyGoodsLabel} title="Remove PDF">
                      <DeleteIcon />
                    </IconButton>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No label uploaded yet
                  </Typography>
                )}
              </Box>

              {heavyGoods.has_label && heavyGoods.weight_threshold === null && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  A label is uploaded but no weight threshold is set — the print button will not
                  appear on DX Shipping until a threshold is saved.
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={2}>
          <Alert severity="info">
            <Typography variant="body2">
              Notification settings coming soon...
            </Typography>
          </Alert>
        </TabPanel>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCode ? 'Edit Code Type' : 'Add New Code Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              fullWidth
              helperText="The code as it appears in Sage (e.g., BOOKIN, DELIVERY-NOTE)"
              disabled={!!editingCode}
            />
            
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              helperText="Friendly name (e.g., 'Booking Contact', 'Delivery Instructions')"
            />
            
            <TextField
              label="Field Label"
              value={formData.field_label}
              onChange={(e) => setFormData({ ...formData, field_label: e.target.value })}
              fullWidth
              helperText="Label for the data field (e.g., 'Phone Number', 'Notes')"
            />
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              helperText="What this code is used for"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  color="success"
                />
              }
              label="Active (will be recognized during import)"
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Example:</strong> If you create a code "BOOKIN" with name "Booking Contact" and 
                field label "Phone Number", then any Sage rows with Product Code = "BOOKIN" will be 
                treated as contact phone numbers instead of products.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {editingCode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;